import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function About() {
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("website_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormSuccess(true);
      form.reset();
      setIsSubmitting(false);
      return;
    }
    formData.delete("website_alt");

    try {
      const response = await fetch(
        "https://readdy.ai/api/form/d9ota3gsenbnioleslgg",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(
            Array.from(formData.entries()).map(([k, v]) => [k, String(v)])
          ).toString(),
        }
      );

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setFormSuccess(true);
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "提交失败，请稍后再试";
        if (serverMsg.toLowerCase().includes("spam")) {
          setFormError("提交失败，请稍后再试。");
        } else {
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormError("网络错误，请检查您的连接后重试。");
    } finally {
      setIsSubmitting(false);
    }
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 韩漫漫画网站",
    description:
      "韩漫漫画网站 —— 专业的韩漫在线观看平台，提供免费韩国漫画下拉式阅读服务。了解我们的品牌故事与联系方式。",
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "韩漫漫画网站",
    url: window.location.origin,
    description:
      "专业的韩漫在线观看平台，提供免费韩国漫画下拉式阅读。韩漫漫画网站致力于为中国读者提供最优质的韩国漫画阅读体验。",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "contact@manhuasite.com",
      availableLanguage: ["Chinese"],
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "韩漫漫画网站是完全免费的吗？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "是的，韩漫漫画网站上的所有韩国漫画均为免费阅读，采用下拉式阅读方式，无需支付任何费用。",
        },
      },
      {
        "@type": "Question",
        name: "如何联系我们？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "您可以通过本页面的联系表单或发送邮件至 contact@manhuasite.com 与我们取得联系。",
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(aboutSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(orgSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema),
        }}
      />
      <Navbar />
      <main className="pt-24 pb-16 min-h-screen">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "关于我们" },
            ]}
          />

          <section className="mb-16">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-50 mb-4">
                关于韩漫漫画网站
              </h1>
              <p className="text-foreground-400 text-base max-w-2xl mx-auto leading-relaxed">
                韩漫漫画网站 —— 您信赖的韩漫在线观看平台。我们致力于为华语读者提供最优质的免费韩国漫画下拉式阅读体验。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              {[
                {
                  icon: "ri-star-line",
                  title: "海量资源",
                  desc: "收录超过5000部优质韩国漫画，涵盖恋爱、奇幻、动作、悬疑等多种类型，满足不同读者的阅读需求。",
                },
                {
                  icon: "ri-flashlight-line",
                  title: "极速更新",
                  desc: "每日同步更新最新韩漫章节，确保读者第一时间追更喜爱的漫画作品，永不落后。",
                },
                {
                  icon: "ri-shield-check-line",
                  title: "安全可靠",
                  desc: "网站采用先进的技术架构和安全措施，保护用户的隐私和数据安全，让您安心阅读。",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="p-6 rounded-xl bg-background-100/70 border border-background-200/50 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/10">
                    <i className={`${item.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-100 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-5 rounded-full bg-primary-500" />
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
                    品牌故事
                  </h2>
                </div>
                <div className="space-y-4 text-foreground-400 leading-relaxed">
                  <p>
                    韩漫漫画网站创立于2020年，由一群热爱韩国漫画的年轻人共同打造。我们的初心很简单——让每一位华语读者都能轻松享受到高质量的韩漫在线观看体验。
                  </p>
                  <p>
                    经过多年的发展，韩漫漫画网站已经成长为国内领先的免费韩国漫画下拉式阅读平台。我们与多家韩国漫画平台建立了合作关系，持续引进优质的韩漫资源，确保网站内容的丰富性和多样性。
                  </p>
                  <p>
                    我们的团队由漫画爱好者、技术工程师和内容运营专家组成，大家各司其职，共同维护着这个漫画韩漫网址的日常运转。无论是内容筛选、翻译校对，还是网站功能优化，我们都力求做到最好。
                  </p>
                  <p>
                    未来，韩漫漫画网站将继续秉持"让韩漫触手可及"的理念，不断优化阅读体验，拓展内容资源，为读者提供更加完善的一站式韩漫在线观看服务。牢记此网址，精彩韩漫随时畅享。
                  </p>
                </div>

                <div className="mt-10 grid grid-cols-3 gap-6">
                  {[
                    { value: "5年+", label: "运营时间" },
                    { value: "5000+", label: "漫画资源" },
                    { value: "百万+", label: "月活用户" },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center p-4 rounded-lg bg-background-100/70 border border-background-200/50">
                      <div className="text-xl md:text-2xl font-heading font-bold text-accent-500">
                        {stat.value}
                      </div>
                      <div className="text-xs text-foreground-500 mt-1 whitespace-nowrap">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-5 rounded-full bg-accent-500" />
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
                    联系我们
                  </h2>
                </div>

                <div className="p-6 rounded-xl bg-background-100/70 border border-background-200/50">
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-500/10">
                        <i className="ri-mail-line text-lg text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">电子邮箱</p>
                        <p className="text-sm text-foreground-200 font-medium">
                          contact@manhuasite.com
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-500/10">
                        <i className="ri-wechat-line text-lg text-secondary-500"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">微信公众号</p>
                        <p className="text-sm text-foreground-200 font-medium">
                          韩漫漫画
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-500/10">
                        <i className="ri-time-line text-lg text-accent-500"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">工作时间</p>
                        <p className="text-sm text-foreground-200 font-medium">
                          周一至周五 9:00 - 18:00
                        </p>
                      </div>
                    </div>
                  </div>

                  {formSuccess ? (
                    <div className="p-6 rounded-lg bg-secondary-500/10 border border-secondary-500/30 text-center">
                      <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-500/20">
                        <i className="ri-check-line text-xl text-secondary-500"></i>
                      </div>
                      <p className="text-foreground-200 font-medium">
                        感谢您的留言！
                      </p>
                      <p className="text-foreground-500 text-sm mt-1">
                        我们已收到您的信息，会尽快与您联系。
                      </p>
                    </div>
                  ) : (
                    <form
                      onSubmit={handleSubmit}
                      data-readdy-form="true"
                      className="space-y-4"
                    >
                      <div>
                        <label
                          htmlFor="about-name"
                          className="block text-sm text-foreground-300 mb-1.5"
                        >
                          您的姓名
                        </label>
                        <input
                          id="about-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/50 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-all duration-200"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="about-email"
                          className="block text-sm text-foreground-300 mb-1.5"
                        >
                          电子邮箱
                        </label>
                        <input
                          id="about-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/50 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-all duration-200"
                          placeholder="请输入您的电子邮箱"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="about-message"
                          className="block text-sm text-foreground-300 mb-1.5"
                        >
                          留言内容
                        </label>
                        <textarea
                          id="about-message"
                          name="message"
                          rows={4}
                          required
                          maxLength={500}
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/50 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-all duration-200 resize-none"
                          placeholder="请输入您的留言内容（500字以内）"
                        ></textarea>
                      </div>

                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />

                      {formError && (
                        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                          <p className="text-sm text-red-400">{formError}</p>
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full px-6 py-3 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 font-semibold text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer"
                      >
                        {isSubmitting ? (
                          <>
                            <i className="ri-loader-4-line animate-spin mr-2"></i>
                            提交中...
                          </>
                        ) : (
                          <>
                            <i className="ri-send-plane-line mr-2"></i>
                            发送留言
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}